import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronDown, X, Package } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

export interface Prop {
  id: string;
  name: string;
  category_id?: string;
  category_name?: string;
  slug?: string;
  thumbnail_url?: string;
  is_custom?: boolean;
}

interface PropsMultiSelectProps {
  selectedPropIds: string[];
  onPropsChange: (propIds: string[]) => void;
  max?: number;
}

const PropsMultiSelect: React.FC<PropsMultiSelectProps> = ({
  selectedPropIds,
  onPropsChange,
  max = 20,
}) => {
  const [open, setOpen] = useState(false);
  const [props, setProps] = useState<Prop[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load props on mount
  useEffect(() => {
    const loadProps = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('props')
          .select(`
            id,
            name,
            slug,
            thumbnail_url,
            is_custom,
            category_id,
            prop_categories (
              name
            )
          `)
          .eq('is_active', true)
          .order('name');

        if (error) {
          console.error('Error loading props:', error);
          return;
        }

        if (data) {
          const transformedProps: Prop[] = data.map(p => ({
            id: p.id,
            name: p.name,
            slug: p.slug || undefined,
            thumbnail_url: p.thumbnail_url || undefined,
            is_custom: p.is_custom || false,
            category_id: p.category_id || undefined,
            category_name: (p.prop_categories as any)?.name || undefined,
          }));
          setProps(transformedProps);
        }
      } catch (error) {
        console.error('Error loading props:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProps();
  }, []);

  // Filter props based on search query
  const filtered = useMemo(() => {
    if (!query) return props;
    const q = query.toLowerCase();
    return props.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.category_name?.toLowerCase().includes(q)
    );
  }, [props, query]);

  // Group props by category for better UX
  const grouped = useMemo(() => {
    const groups: Record<string, Prop[]> = {};
    filtered.forEach(prop => {
      const category = prop.category_name || 'Uncategorized';
      if (!groups[category]) groups[category] = [];
      groups[category].push(prop);
    });
    return groups;
  }, [filtered]);

  // Get selected props for display
  const selectedProps = useMemo(() => {
    return props.filter(p => selectedPropIds.includes(p.id));
  }, [props, selectedPropIds]);

  const addProp = (propId: string) => {
    if (selectedPropIds.includes(propId)) return;
    if (selectedPropIds.length >= max) return;
    onPropsChange([...selectedPropIds, propId]);
  };

  const removeProp = (propId: string) => {
    onPropsChange(selectedPropIds.filter(id => id !== propId));
  };

  return (
    <div className="space-y-2">
      {/* Selected props badges */}
      <div className="flex flex-wrap gap-2">
        {selectedProps.map(prop => (
          <Badge key={prop.id} variant="secondary" className="flex items-center gap-1">
            {prop.thumbnail_url && (
              <img
                src={prop.thumbnail_url}
                alt={prop.name}
                className="h-4 w-4 rounded object-cover"
              />
            )}
            {prop.name}
            {prop.category_name && (
              <span className="text-xs text-muted-foreground">({prop.category_name})</span>
            )}
            <button
              type="button"
              onClick={() => removeProp(prop.id)}
              aria-label={`Remove ${prop.name}`}
              className="ml-1 hover:bg-destructive/20 rounded-sm"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>

      {/* Add props dropdown */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="justify-between w-full"
          >
            <span className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Add prop{selectedPropIds.length > 0 ? ` (${selectedPropIds.length}/${max})` : ''}
            </span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[400px]" align="start">
          <Command>
            <CommandInput
              placeholder="Search props..."
              value={query}
              onValueChange={setQuery}
            />
            {isLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading props...
              </div>
            ) : (
              <>
                <CommandEmpty>
                  <div className="p-3 text-sm text-muted-foreground">
                    No props found matching "{query}"
                  </div>
                </CommandEmpty>
                <CommandList className="max-h-[300px]">
                  {Object.keys(grouped).sort().map(category => (
                    <CommandGroup key={category} heading={category}>
                      {grouped[category].map(prop => {
                        const isSelected = selectedPropIds.includes(prop.id);
                        const isMaxReached = selectedPropIds.length >= max;
                        const isDisabled = !isSelected && isMaxReached;

                        return (
                          <CommandItem
                            key={prop.id}
                            value={prop.name}
                            onSelect={() => {
                              if (!isDisabled) {
                                addProp(prop.id);
                                setQuery('');
                              }
                            }}
                            disabled={isDisabled}
                            className={isSelected ? 'opacity-50' : ''}
                          >
                            <div className="flex items-center gap-2 w-full">
                              {prop.thumbnail_url && (
                                <img
                                  src={prop.thumbnail_url}
                                  alt={prop.name}
                                  className="h-6 w-6 rounded object-cover"
                                />
                              )}
                              <span className="flex-1">{prop.name}</span>
                              {isSelected && (
                                <Badge variant="secondary" className="text-xs">Selected</Badge>
                              )}
                              {prop.is_custom && (
                                <Badge variant="outline" className="text-xs">Custom</Badge>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))}
                </CommandList>
              </>
            )}
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default PropsMultiSelect;
